import axios from "axios";
import { getCookie, Auth } from "./lib/cookie";

export const client = axios.create({
  baseURL: `${process.env.HOST}/api/`
});

export function getAccessToken() {
  return getCookie(Auth.AccessToken);
}

client.interceptors.request.use(config => {
  const token = getAccessToken();

  if (!token) {
    return config;
  }

  return {
    ...config,
    headers: { ...config.headers, Authorization: `Bearer ${token}` }
  };
});

/**
 * @param {string} signedURL
 * @param {File} file
 * @return {void}
 */
async function uploadToGoogleStorage(signedURL, file) {
  const headers = { "content-type": file.type };
  await axios.put(signedURL, file, { headers });
}

/**
 * @typedef {Object} GetUploadImageMetaDataResponse
 * @property {string} url
 * @property {string} key
 *
 * @param {string} imageType
 * @param {string} imageName
 * @param {string} featureName
 *
 * @return {GetUploadImageMetaDataResponse}
 */
async function getUploadImageMetaData(imageType, imageName, featureName) {
  const params = {
    imageType,
    imageName,
    featureName
  };

  const { data } = await client.get("/uploads/image", { params });
  const { url, imageKey: key } = data;

  return { url, key };
}

/**
 * @param {string} pdfKey
 *
 * @return {string}
 */
export async function getPDF(pdfKey, featureName) {
  try {
    const params = {
      fileKey: pdfKey,
      feature: featureName,
      contentType: "application/pdf"
    }

    const { data } = await client.get("/downloads/file", { params });
    const { url, imageKey: key } = data;

    return url;
  } catch (error) {
    console.debug("Download pdf failed with key:", pdfKey)
  }
}

/**
 * @typedef {Object} GetUploadFileMetaDataResponse
 * @property {string} url
 * @property {string} key
 *
 * @param {string} fileType
 * @param {string} featureName
 *
 * @return {GetUploadFileMetaDataResponse}
 */
async function getUploadFileMetaData(fileType, featureName) {
  const params = {
    contentType: fileType,
    fileName: `${featureName}file${Date.now()}`,
    feature: featureName
  };

  const { data } = await client.get("/uploads/file", { params });
  const { url, fileKey: key } = data;

  return { url, key };
}

/**
 * @param {File} image
 * @param {string} imageName
 * @param {string} featureName
 *
 * @return {string}
 */
export async function uploadImage(image, name, featureName) {
  const imageName = name || image.name;
  const pathName = featureName || "article";
  const imageType = image.type;

  const { url, key } = await getUploadImageMetaData(
    imageType,
    imageName,
    pathName
  );
  await uploadToGoogleStorage(url, image);

  return key;
}

 export async function uploadImageAndGetUrlAndKey(image, name, featureName) {
  const imageKey = await uploadImage(image, name, featureName)

  const params = { imageKey };
  const dataKey  = await client.get("/uploads/images/url", { params });

  const { data } = dataKey;

  return data.key;
}

/**
 * @param {File} file
 * @param {string} featureName
 *
 * @return {string}
 */
export async function uploadFile(file, featureName) {
  const fileType = file.type;

  const { url, key } = await getUploadFileMetaData(
    fileType,
    featureName
  );
  await uploadToGoogleStorage(url, file);

  return key;
}

export async function getPublicImageUrl(imageKey) {
  const params = { imageKey };
  const { data } = await client.get("/uploads/images/public", { params });
  const { url } = data;

  return { url };
}

/**
 * @param {File} image
 * @param {string} imageName
 * @param {string} featureName
 *
 * @return {string}
 */
export async function uploadPublicImage(file, name, pathImage) {
  const imageName = name || file.name || "article";
  const path = pathImage || "article";

  const imageKey = await uploadImage(file, imageName, path);
  const { url } = await getPublicImageUrl(imageKey);

  return url;
}

/**
 * @param {File} file
 * @param {string} fileName
 * @param {string} featureName
 *
 * @return {string}
 */
export async function uploadPublicFile(file, featureName) {
  const fileKey = await uploadFile(file, featureName);

  return fileKey;
}

/**
 * @param {File} image
 *
 * @return {string}
 */
export async function uploadBannerCoverImage(image) {
  const url = await uploadPublicImage(image);

  return url;
}

/**
 * @param {Blob} file
 * @param {string} fileName
*/
export function getDownloadedReport(file, fileName) {
  const blob = new Blob([file]);
  const link = document.createElement("a");

  link.href = window.URL.createObjectURL(blob);
  link.download = fileName;
  link.click();
}

/**
 *
 * @param {string} articleId
 * @param {string} imageKey
 *
 * @return {void}
 */
async function createBanner(articleId, imageKey) {
  const params = { article_id: articleId, image: imageKey };

  await client.post("/admin/banners", params, {
    headers: { Authorization: `Bearer: ${getAccessToken()}` }
  });
}

/**
 * @typedef {Object} Banner
 * @property {string} article_id
 * @property {string} image_url
 *
 * @param {string} articleId
 *
 * @return {Banner}
 */
export async function getBanner(articleId) {
  const { data } = await client.get(`/admin/banners/${articleId}`);

  return data.result;
}

export async function getVoucher(articleId) {
  const { data } = await client.get(`/admin/ruejai_club/packages/voucher/${articleId}`);

  return data.result;
}

/**
 *
 * @param {string} articleId
 * @param {File} image
 *
 * @return {void}
 */
export async function setAsBanner(articleId, image) {
  const imageKey = await uploadBannerCoverImage(image);

  await createBanner(articleId, imageKey);
}

export const articleStatus = {
  draft: "draft",
  toBeApproved: "toBeApproved",
  toBePublished: "toBePublished",
  published: "published"
};



// WEBPACK FOOTER //
// ./src/god.js